import { getApiUri } from "@/api/shared.ts" with { types: "comptime" };
import { authenticatedFetch, throwBody } from "@/api/shared.ts";
import { useUserStore } from "@/stores/useUserStore.ts";
import type { UserDetails } from "@/types/user.ts";
import type { TutorDetails } from "@/types/tutor.ts";

export const getMyDetails = async (): Promise<UserDetails> => {
  const { Logout } = useUserStore.getState();
  // TODO: Move ? to optional
  const response = await authenticatedFetch(getApiUri("/v4/users/me"), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    priority: "high",
  });

  if (!response.ok) {
    // 401 is already handled by authenticatedFetch. A 403 here means we're not an
    // admin, which is equally fatal for this app, so bail out too.
    if (response.status === 403) {
      Logout(true);
    }
    throw await throwBody(response);
  }

  return response.json();
};

export const getTutorDetails = async (lowPriority?: boolean): Promise<TutorDetails> => {
  const { userDetails, Logout } = useUserStore.getState();

  if (userDetails == null || (userDetails.userId && userDetails?.userId === -1)) {
    throw new Error(`Invalid user id, ${userDetails?.userId} provided`);
  }

  const response = await authenticatedFetch(
    getApiUri("/v4/admin/users/" + userDetails?.userId + "/tutors"),
    {
      priority: lowPriority ? "low" : "high",
    },
  );

  if (!response.ok) {
    if (response.status === 403) {
      Logout(true);
    }
    throw await throwBody(response);
  }

  return response.json();
};

interface UserAvatarResponse {
  userAvatarId: number;
  userId: number;
  dateCreated: Date;
  dateDeleted: Date | null;
  json: string | null;
  imageURL: string | null;
  thumbnailURL: string | null;
  isActive: boolean | null;
}

export const getUserAvatar = async (userId: number): Promise<string | null> => {
  const response = await authenticatedFetch(getApiUri(`/v3/users/${userId}/avatar`), {
    priority: "low",
  });

  if (!response.ok) {
    throw await throwBody(response);
  }

  if (response.status === 204) return null;

  const result: UserAvatarResponse = await response.json();

  return result.imageURL ?? result.thumbnailURL;
};
